// Keep the bridge envelope in sync with Frontend/src/pages/public/SalesDemo/salesDemoBridge.ts.
// No project key, account token or API host crosses this bridge.
export const SALES_DEMO_CHANNEL = "gleap-sales-demo-v1";
export const isSalesDemoMode = () => window.location.pathname.replace(/\/+$/, "") === "/sales-demo";

export const salesDemoParentOrigin = (value: string | null): string | null => {
  try {
    const url = new URL(value ?? "");
    if (url.origin !== value || url.username || url.password) return null;
    if (url.protocol === "https:" && ["app.gleap.ai", "app.gleap.io"].includes(url.hostname)) return url.origin;
    if (["http:", "https:"].includes(url.protocol) && ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname)) return url.origin;
  } catch {}
  return null;
};

export const isSalesDemoMessage = (event: Pick<MessageEvent, "origin" | "source" | "data">, origin: string, parent: Window, channel: string) =>
  event.origin === origin && event.source === parent &&
  !!event.data && typeof event.data === "object" &&
  event.data.channel === SALES_DEMO_CHANNEL && event.data.instance === channel;

let targetOrigin: string | null = null;
let instance = "";
let lastAnchors = "";
let lastStatus = "";
export const configureSalesDemoBridge = (origin: string, channel: string) => { targetOrigin = origin; instance = channel; lastAnchors = ""; lastStatus = ""; };
export const sendSalesDemoMessage = (type: string, data?: unknown) => {
  if (!targetOrigin) return;
  window.parent.postMessage({ channel: SALES_DEMO_CHANNEL, instance, type, data }, targetOrigin);
};

// Home menu items that start a scripted story on the page instead of asking Kai.
// They open no chat and spend no question: the page plays the story.
export const SALES_DEMO_STORY_BUG = "demo-story-bug";
const STORY_ITEMS = new Map<unknown, "bug">([[SALES_DEMO_STORY_BUG, "bug"]]);
export const startSalesDemoStory = (botId: unknown): boolean => {
  const kind = STORY_ITEMS.get(botId);
  if (!kind) return false;
  sendSalesDemoMessage("story", { kind });
  return true;
};

// Where the page's tour cards point, in the widget iframe's viewport px: the home question box, the story row, the
// suggested questions without the story row (`suggestions`, one box round all of them) and "Speak to Kai" (`voice`,
// inside the question box).
export interface SalesDemoRect { x: number; y: number; width: number; height: number }
export interface SalesDemoAnchors { input?: SalesDemoRect; story?: SalesDemoRect; suggestions?: SalesDemoRect; voice?: SalesDemoRect }
type ViewportSize = { width: number; height: number };

/** Whole px, non-empty and at least partly on screen; anything else is no anchor. */
export const salesDemoRect = (rect: Pick<DOMRect, "left" | "top" | "width" | "height"> | null | undefined, viewport: ViewportSize): SalesDemoRect | undefined => {
  if (!rect || ![rect.left, rect.top, rect.width, rect.height].every(Number.isFinite) || rect.width < 1 || rect.height < 1) return undefined;
  if (rect.left + rect.width <= 0 || rect.top + rect.height <= 0 || rect.left >= viewport.width || rect.top >= viewport.height) return undefined;
  return { x: Math.round(rect.left), y: Math.round(rect.top), width: Math.round(rect.width), height: Math.round(rect.height) };
};

/** Posts the anchors only when they changed since the last post. */
export const sendSalesDemoAnchors = (anchors: SalesDemoAnchors): boolean => {
  const key = JSON.stringify(anchors);
  if (!targetOrigin || key === lastAnchors) return false;
  lastAnchors = key;
  sendSalesDemoMessage("anchors", anchors);
  return true;
};

// The widget's state the page's tour and story follow (the `status` message): the home is on screen, a Kai Voice
// call runs (no story then; `call` going false is the call ending), the call screen covers the widget (its own
// controls, not the home, are what to use), Kai's reply to the latest question is in the conversation (nothing pending).
export interface SalesDemoStatus { home: boolean; call: boolean; callScreen: boolean; answered: boolean }
const CALL_RUNNING = ["requestingMic", "connecting", "active", "ending"];
export const isSalesDemoCallRunning = (callState: string) => CALL_RUNNING.includes(callState);
export const isSalesDemoAnswered = ({ messages, pending }: { messages: { role: string }[]; pending: string }) =>
  !pending && messages[messages.length - 1]?.role === "assistant";
export const salesDemoStatus = ({ page, callState, answered = false }: { page: string; callState: string; answered?: boolean }): SalesDemoStatus =>
  ({ home: page === "menu", call: isSalesDemoCallRunning(callState), callScreen: callState !== "idle", answered });

/** Posts the status only when it changed since the last post. */
export const sendSalesDemoStatus = (status: SalesDemoStatus): boolean => {
  const key = JSON.stringify(status);
  if (!targetOrigin || key === lastStatus) return false;
  lastStatus = key;
  sendSalesDemoMessage("status", status);
  return true;
};

// Page -> widget commands: `home` shows the home from its top and clears a finished call's screen (the tour points
// at the home),
// `hangUp` ends any call (the story starts),
// `call` starts a Kai Voice call like "Speak to Kai" on the home, `showStory` scrolls the home until the story row
// shows (it is last in the list, below the fold on a small phone).
export enum SalesDemoCommand {
  Home = "home",
  HangUp = "hangUp",
  Call = "call",
  ShowStory = "showStory",
}
export const salesDemoCommand = (type: unknown): SalesDemoCommand | null =>
  Object.values(SalesDemoCommand).find((value) => value === type) ?? null;

export const openSalesDemoUrl = (value: unknown) => {
  try {
    const url = new URL(String(value));
    if (!["https:", "http:"].includes(url.protocol) || url.username || url.password) return;
    window.open(url.href, "_blank", "noopener,noreferrer");
  } catch {}
};

// The regular SDK message listener stays disconnected in demo mode. Only these
// visual events leave the iframe; there are no SDK actions or live ticket calls.
export const handleSalesDemoWidgetMessage = (message: { name?: string; data?: unknown }) => {
  if (message.name === "close-widget") sendSalesDemoMessage("close");
  if (message.name === "open-url") openSalesDemoUrl(message.data);
};
